// src/modules/app-views/omnichannel/service.js
// *******************************************************************************
// OMNICHANNEL SERVICE
// *******************************************************************************
// API client for conversation management (channel-agnostic)
// *******************************************************************************
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';

// *******************************************************************************
// SERVICE
// *******************************************************************************
const omnichannelService = {
  // Paginated list of conversations (with optional filters via query string)
  getConversations: ({ size = 100, filter } = {}) => {
    const payload = { pagination: { size } };
    if (filter) payload.filter = filter;
    const q = encodeURIComponent(btoa(JSON.stringify(payload)));
    return axiosService.get({ url: `${API.OMNICHANNEL.PAGINATION}?q=${q}` });
  },

  // Single conversation by ID (hydrated with contact data)
  getConversation: id =>
    axiosService.get({ url: API.OMNICHANNEL.URL.replace(':_id?', id), returnAsObject: true }),

  // Create a new conversation
  createConversation: data =>
    axiosService.post({ url: API.OMNICHANNEL.URL.replace(':_id?', ''), data }),

  // Find or create a conversation by phone number (outbound initiation from CRM)
  getOrCreateByPhone: data =>
    axiosService.post({ url: `${API.OMNICHANNEL.URL.replace(':_id?', '')}by-phone`, data, returnAsObject: true }),

  // Update conversation metadata (tags, status, assignedTo, etc.)
  updateConversation: (id, data) =>
    axiosService.put({ url: API.OMNICHANNEL.URL.replace(':_id?', id), data }),

  // Save audio duration from browser metadata (fire-and-forget, best-effort)
  updateMessageDuration: (conversationId, externalMessageId, duration) => {
    axiosService.put({
      url: `/v1/omnichannel/message-duration/${conversationId}`,
      data: { externalMessageId, duration },
    }).catch(() => {});
  },

  // Send a message in a conversation (push to messages array)
  sendMessage: (id, message) =>
    axiosService.put({
      url: API.OMNICHANNEL.ADD_TO_SET
        .replace(':_id', id)
        .replace(':operator', '$push')
        .replace(':path', 'messages'),
      data: message,
    }),

  // Mark all messages in a conversation as read
  markAsRead: id =>
    axiosService.put({ url: API.OMNICHANNEL.MARK_AS_READ.replace(':_id', id) }),

  // Assign conversation to an agent
  assignConversation: (id, assignedTo) =>
    axiosService.put({
      url: API.OMNICHANNEL.ASSIGN.replace(':_id', id),
      data: { assignedTo },
    }),

  // Close conversation (status → resolved)
  closeConversation: id =>
    axiosService.put({ url: API.OMNICHANNEL.CLOSE.replace(':_id', id) }),

  // Transfer conversation to another agent
  transferConversation: (id, data) =>
    axiosService.put({
      url: API.OMNICHANNEL.TRANSFER.replace(':_id', id),
      data,
    }),

  // Close active context of a conversation
  closeContext: (id, data) =>
    axiosService.put({
      url: API.OMNICHANNEL.CLOSE_CONTEXT.replace(':_id', id),
      data,
    }),

  // Escalate context to CRM/ticket/order
  escalateContext: (id, data) =>
    axiosService.put({
      url: API.OMNICHANNEL.ESCALATE_CONTEXT.replace(':_id', id),
      data,
    }),

  // Open a new context on a conversation
  openNewContext: (id, data) =>
    axiosService.post({
      url: API.OMNICHANNEL.NEW_CONTEXT.replace(':_id', id),
      data,
    }),

  // Translate a single message in a conversation (used for per-message manual toggle)
  translateMessage: (id, data) =>
    axiosService.post({
      url: API.OMNICHANNEL.TRANSLATE.replace(':_id', id),
      data,
      returnAsObject: true,
    }),

  // Translate arbitrary text before sending (outbound pre-send translation).
  // Validates conversation ownership, then calls the translate service.
  // Request:  { text, targetLang }
  // Response: { text, lang, provider, detectedSourceLang }
  translateText: (id, data) =>
    axiosService.post({
      url: API.OMNICHANNEL.TRANSLATE_TEXT.replace(':_id', id),
      data,
      returnAsObject: true,
    }),

  // Translate all inbound messages in a conversation in one request.
  // Backend resolves cache hits and translates only what's missing, returning everything at once.
  // Request:  { targetLang, messageIndices: number[] }
  // Response: { translations: { [idx]: { text, lang, provider, cached } } }
  translateBatch: (id, data) =>
    axiosService.post({
      url: API.OMNICHANNEL.TRANSLATE_BATCH.replace(':_id', id),
      data,
      returnAsObject: true,
    }),

  // Send outbound text message (routes via sourceType: Meta or Baileys).
  // originalText: typed text before pre-send translation (omit when no delivery lang was set).
  // deliveryLang: language code the message was translated into before sending.
  sendWhatsappMessage: (id, text, sourceOptions, originalText, deliveryLang) =>
    axiosService.post({
      url: API.OMNICHANNEL.SEND_MESSAGE.replace(':_id', id),
      data: {
        text,
        ...(originalText ? { originalText } : {}),
        ...(deliveryLang ? { deliveryLang } : {}),
        ...sourceOptions,
      },
      returnAsObject: true,
    }),

  // ─────────────────────────────────────────────────────────────────────────
  // WHATSAPP CONFIG (WABA phone management — Meta official)
  // ─────────────────────────────────────────────────────────────────────────

  // List all registered WhatsApp phones for the current tenant
  getWhatsappPhones: () =>
    axiosService.get({ url: API.OMNICHANNEL_WHATSAPP_CONFIG.URL.replace('/:_id?', '') }),

  // Register a new WhatsApp Business phone
  addWhatsappPhone: data =>
    axiosService.post({ url: API.OMNICHANNEL_WHATSAPP_CONFIG.URL.replace('/:_id?', ''), data }),

  // Update phone config (isActive, tokens, displayName, etc.)
  updateWhatsappPhone: (id, data) =>
    axiosService.put({ url: API.OMNICHANNEL_WHATSAPP_CONFIG.URL.replace(':_id?', id), data }),

  // Remove a phone from the tenant
  removeWhatsappPhone: id =>
    axiosService.delete({ url: API.OMNICHANNEL_WHATSAPP_CONFIG.URL.replace(':_id?', id) }),

  // Update WhatsApp Business Profile (picture + about) via Meta Graph API
  updateWhatsappProfile: (id, data) =>
    axiosService.put({ url: API.OMNICHANNEL_WHATSAPP_CONFIG.PROFILE.replace(':_id', id), data, returnAsObject: true }),

  // Get WhatsApp Business Profile from Meta
  getWhatsappProfile: id =>
    axiosService.get({ url: API.OMNICHANNEL_WHATSAPP_CONFIG.PROFILE.replace(':_id', id), returnAsObject: true }),

  // ─────────────────────────────────────────────────────────────────────────
  // BAILEYS CONFIG (Evolution API — unofficial WhatsApp)
  // ─────────────────────────────────────────────────────────────────────────

  // List all Baileys instances for the current tenant
  getBaileysInstances: () =>
    axiosService.get({ url: API.OMNICHANNEL_BAILEYS_CONFIG.URL.replace('/:_id?', '') }),

  // Create a new Baileys instance (returns QR code)
  createBaileysInstance: data =>
    axiosService.post({ url: API.OMNICHANNEL_BAILEYS_CONFIG.URL.replace('/:_id?', ''), data, returnAsObject: true }),

  // Get QR code + connection status (polling endpoint)
  getBaileysQr: id =>
    axiosService.get({ url: API.OMNICHANNEL_BAILEYS_CONFIG.QR.replace(':_id', id), returnAsObject: true }),

  // Update Baileys instance (displayName, isActive, reconnect)
  updateBaileysInstance: (id, data) =>
    axiosService.put({ url: API.OMNICHANNEL_BAILEYS_CONFIG.URL.replace(':_id?', id), data }),

  // Remove a Baileys instance
  removeBaileysInstance: id =>
    axiosService.delete({ url: API.OMNICHANNEL_BAILEYS_CONFIG.URL.replace(':_id?', id) }),

  // ─────────────────────────────────────────────────────────────────────────
  // UNIFIED SOURCES (Meta + Baileys)
  // ─────────────────────────────────────────────────────────────────────────

  // List all active sources for the current tenant
  getSources: () =>
    axiosService.get({ url: API.OMNICHANNEL_SOURCES.URL }),

  // Force-refresh contact avatar (bypasses 7-day cache)
  refreshAvatar: id =>
    axiosService.put({ url: API.OMNICHANNEL.REFRESH_AVATAR.replace(':_id', id), returnAsObject: true }),

  // Send emoji reaction to a message (or remove if emoji is null/empty)
  // Request:  { messageIndex: number, emoji: string }
  // Response: { reactions: [{ emoji, senderName }] }
  sendReaction: (id, data) =>
    axiosService.post({
      url: API.OMNICHANNEL.SEND_REACTION.replace(':_id', id),
      data,
      returnAsObject: true,
    }),

  // Sync archived status from Evolution API (fire-and-forget, called after pagination)
  syncArchived: () =>
    axiosService.post({ url: API.OMNICHANNEL.SYNC_ARCHIVED }),

  // Mark orphan conversations (linked to deleted sources) as 'orphan' status
  cleanupOrphans: () =>
    axiosService.post({ url: API.OMNICHANNEL.CLEANUP_ORPHANS, returnAsObject: true }),
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default omnichannelService;
